import { AppStore } from '../state/store';
import { TrackWarning } from '../core/track/validate';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { sidebarPane } from './sidebarTabs';

/** Right-hand panel: selection summary + warnings list. */
export class Inspector {
  private el: HTMLElement;
  private warnings: TrackWarning[] = [];
  private rollback: { s: number; message: string } | null = null;
  onWarningClick: ((s: number) => void) | null = null;
  onAutoSolve: (() => void) | null = null;

  constructor(private store: AppStore) {
    this.el = document.createElement('div');
    sidebarPane('build').appendChild(this.el);
    store.on('selection', () => this.render());
    store.on('track', () => this.render());
    store.on('doc', () => this.render());
    this.render();
  }

  setWarnings(warnings: TrackWarning[], rollback: { s: number; message: string } | null): void {
    this.warnings = warnings;
    this.rollback = rollback;
    this.render();
  }

  private render(): void {
    const idx = this.store.selectedIndices();
    const doc = this.store.doc;
    let html = '';

    if (this.store.mode === 'build') {
      html += panelStart('selection', 'Selection');
      if (idx.length === 0) {
        html += `<div class="row"><label>Nothing selected — click a node, or press Insert to add a piece.</label></div>`;
        html += `<div class="row"><label>Nodes: ${doc.track.nodes.length} · Length: ${this.store.runtime.totalLength.toFixed(0)} m</label></div>`;
      } else if (idx.length === 1) {
        html += `<div class="row"><label>Node ${idx[0]} selected</label></div>`;
        html += `<div class="hint">Edit position and orientation in the Track panel.</div>`;
      } else {
        html += `<div class="row"><label>${idx.length} nodes selected</label></div>`;
      }
      html += panelEnd();
    }

    html += panelStart('checks', 'Checks');
    const canSolve = this.warnings.some(
      (w) => w.kind === 'curvature-step' || w.kind === 'roll-rate' || w.kind === 'below-ground',
    );
    if (canSolve) {
      html += `<div class="row"><button type="button" data-action="auto-solve">Auto solve</button></div>`;
    }
    if (this.rollback) {
      html += `<div class="warning-item error" data-s="${this.rollback.s}">${escapeHtml(this.rollback.message)}</div>`;
    } else if (doc.track.closed) {
      html += `<div class="ok-item">Circuit completes — no rollback</div>`;
    }
    if (this.warnings.length === 0) {
      html += `<div class="ok-item">No geometry warnings</div>`;
    } else {
      for (const w of this.warnings.slice(0, 12)) {
        html += `<div class="warning-item" data-s="${w.s}">${escapeHtml(w.message)}</div>`;
      }
      if (this.warnings.length > 12) {
        html += `<div class="row"><label>… and ${this.warnings.length - 12} more</label></div>`;
      }
    }
    html += panelEnd();

    this.el.innerHTML = html;
    bindPanelCollapse(this.el);

    this.el.querySelectorAll<HTMLElement>('.warning-item[data-s]').forEach((item) => {
      item.addEventListener('click', () => this.onWarningClick?.(parseFloat(item.dataset.s!)));
    });
    this.el.querySelector<HTMLButtonElement>('[data-action="auto-solve"]')?.addEventListener('click', () => {
      this.onAutoSolve?.();
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
